import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import IconDown from '../../icons/down';
import IconSelected from '../../icons/selected';
import IconUp from '../../icons/up';
import IconChangeLoClock from '../../icons/change-log-clock';
import ButtonSecondary from '../../button/button-secondary';
import Popup from '../../popup/popup';
import Select from '../../select/select';
import Textarea from '../../textarea/textarea';
import ToggleInput from '../../checkbox/toggle';
import IconHideAll from '../../icons/hide-all';
import IconExpandAll from '../../icons/expand-all';
import { ButtonSize } from '../../button/types';
import { PermissionEnum } from '../../../base-types';
import {
  TransactionWarning,
  TransactionWarningActionEnum,
  MatchStatusEnum,
} from '../types';
import { WarningGroupsTypes } from './types';
import BlacklistWarningGroups from './blacklist-warning-groups';
import MonitoringWarningGroups from './monitoring-warning-groups';

import cn from './styles.module.css';

export default function WarningGroups({
  warningsGroups,
  applyWarning,
  applyWarningInProgress,
  batchApplyWarningInProgress,
  batchApplyWarning,
  applyError,
  setBlacklistDataVisible,
  setBlacklistData,
  setPanelIsOpen,
  auditLogAvailable,
  batchApplyWarningIsSuccess,
  onResetTransactionWarningAction,
  resetPaymentOrderTransactionActionIsLoading,
  resetPaymentOrderTransactionActionIsSuccess,
  selectedWarningId,
  getPaymentOrderWarningsAction,
  paginationInProgress,
  selectedWarningGroup,
  setBlacklistDataItem,
  setBlacklistItemVisible,
  getPaymentOrderWarningById,
  isWarningByIdLoading,
  isWarningByIdFetching,
  activePermissions,
  employee,
  transactionsTypes,
  getWarningGroupBeneficiaries,
  beneficiariesIsLoading,
  warningsInfo,
  selectedMonitoringRuleWarningId,
  hideApproveActions,
  hideRejectActions,
}: WarningGroupsTypes) {
  const [warningsWithoutActions, setWarningsWithoutActions] = useState<
    TransactionWarning[]
  >([]);
  const [checkedBlockLists, setCheckedBlockLists] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [collapsed, setCollapsed] = useState(false);
  const [applyAllPopupVisible, setApplyAllPopupVisible] = useState(false);
  const [comment, setComment] = useState('');
  const [action, setAction] = useState('');
  const [matchStatus, setMatchStatus] = useState('');
  const [showPassed, setShowPassed] = useState(true);
  const [hideAll, setHideAll] = useState(true);
  const [hiddenGroupsIds, setHiddenGroupsIds] = useState<string[] | undefined>(
    undefined,
  );

  const blacklistWarningGroups = warningsGroups?.filter(
    (item) => item.type === 'BLACKLIST',
  );
  const monitoringWarningGroups = warningsGroups?.filter(
    (item) => item.type === 'MONITORING',
  );

  const onApplyActions = async () => {
    await batchApplyWarning({
      action,
      comment,
      matchStatus,
    });
  };

  useEffect(() => {
    const warningsGroupsArray: TransactionWarning[] = [];
    const blockLists: { id: string; name: string }[] = [];

    warningsGroups?.forEach((item) => {
      item?.warnings?.edges?.forEach((warning) => {
        if (!warning.node.action) {
          warningsGroupsArray.push(warning.node);
        }
      });

      item.warningsGroupsBlacklists?.edges?.forEach((blocklist) => {
        if (
          !blockLists.find((list) => list.id === blocklist.node.blacklistId)
        ) {
          blockLists.push({
            id: blocklist.node.blacklistId,
            name: blocklist.node.blacklist.name,
          });
        }
      });
    });

    setCheckedBlockLists(blockLists);
    setWarningsWithoutActions(warningsGroupsArray);
  }, [warningsGroups]);

  useEffect(() => {
    if (batchApplyWarningIsSuccess) {
      setApplyAllPopupVisible(false);
      setComment('');
      setAction('');
      setMatchStatus('');
    }
  }, [batchApplyWarningIsSuccess]);

  useEffect(() => {
    if (warningsGroups?.length) {
      if (
        !hideAll &&
        hiddenGroupsIds &&
        hiddenGroupsIds?.length === warningsGroups?.length
      ) {
        setHideAll(true);
      } else if (hideAll && hiddenGroupsIds && hiddenGroupsIds?.length === 0) {
        setHideAll(false);
      }
    }
  }, [hideAll, hiddenGroupsIds, warningsGroups]);

  useEffect(() => {
    if (!hiddenGroupsIds && warningsGroups) {
      setHiddenGroupsIds(warningsGroups?.map((item) => item.id));
    }
  }, [hiddenGroupsIds, warningsGroups]);

  return (
    <div>
      {!warningsGroups?.find((item) => item.warnings?.edges.length) && (
        <div className={cn.warningsTitle}>No matches found</div>
      )}
      {warningsGroups?.find((item) => item.warnings?.edges?.length) && (
        <>
          <div className={cn.warningsTitle}>
            Found matches that require a solution
            {auditLogAvailable && (
              <NavLink
                to={`/administration/change-logs?entityType=WARNING_GROUP&entityId=${warningsGroups[0].id}`}
                className={cn.changeLogLink}
                target="_blank"
              >
                <IconChangeLoClock />
                Change Log
              </NavLink>
            )}
          </div>
          <div className={cn.warningActions}>
            {activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_INCOMING_AML_APPROVE_ACTION ||
                activePermissions.includes(
                  PermissionEnum.TRANSACTIONS_INCOMING_AML_REJECT_ACTION,
                ),
            ) &&
              warningsWithoutActions?.length > 0 && (
                <ButtonSecondary
                  size={ButtonSize.SMALL}
                  text="Apply to all matches"
                  className={`button-white ${cn.applyAll}`}
                  onClick={() => setApplyAllPopupVisible(true)}
                />
              )}
          </div>
        </>
      )}
      <div className={cn.warningsControlsWrapper}>
        {warningsInfo && (
          <div className={cn.warningsTotalContent}>
            {`${warningsInfo?.entitySummary?.total} warnings`}
            <div className={cn.circle} />
            {`${
              warningsInfo.entitySummary.total -
              warningsInfo.entitySummary.unprocessed
            } passed`}
          </div>
        )}

        <div className={cn.warningsControls}>
          <ToggleInput
            name="showPassed"
            checked={showPassed}
            onChange={(e) => setShowPassed(e.target.checked)}
            title="Show passed"
          />
          <button
            type="button"
            className={cn.hideAllButton}
            onClick={() => {
              setHideAll(!hideAll);

              if (!hideAll) {
                setHiddenGroupsIds(
                  warningsGroups?.map((item) => item.id),
                );
              } else {
                setHiddenGroupsIds([]);
              }
            }}
          >
            {hideAll ? <IconExpandAll /> : <IconHideAll />}
          </button>
        </div>
      </div>
      {blacklistWarningGroups && blacklistWarningGroups?.length > 0 && (
        <BlacklistWarningGroups
          groups={blacklistWarningGroups}
          currentEmployee={employee}
          resetPaymentOrderTransactionActionIsLoading={
            resetPaymentOrderTransactionActionIsLoading
          }
          activePermissions={activePermissions}
          setBlacklistDataItem={setBlacklistDataItem}
          setBlacklistData={setBlacklistData}
          applyWarningInProgress={applyWarningInProgress}
          onResetTransactionWarningAction={onResetTransactionWarningAction}
          applyWarning={applyWarning}
          setBlacklistDataVisible={setBlacklistDataVisible}
          setPanelIsOpen={setPanelIsOpen}
          setBlacklistItemVisible={setBlacklistItemVisible}
          resetPaymentOrderTransactionActionIsSuccess={
            resetPaymentOrderTransactionActionIsSuccess
          }
          selectedWarningId={selectedWarningId}
          getPaymentOrderWarningsAction={getPaymentOrderWarningsAction}
          paginationInProgress={paginationInProgress}
          selectedWarningGroup={selectedWarningGroup}
          getPaymentOrderWarningById={getPaymentOrderWarningById}
          isWarningByIdLoading={isWarningByIdLoading}
          isWarningByIdFetching={isWarningByIdFetching}
          setHiddenGroupsIds={setHiddenGroupsIds}
          hiddenGroupsIds={hiddenGroupsIds}
          hideAll={hideAll}
          showPassed={showPassed}
          warningsInfo={warningsInfo}
          hideApproveActions={hideApproveActions}
          hideRejectActions={hideRejectActions}
        />
      )}
      <button
        type="button"
        className={`${cn.blockListsButton} ${
          collapsed ? cn.withoutBorder : ''
        }`}
        onClick={() => setCollapsed(!collapsed)}
      >
        {`Checked blocklists (${checkedBlockLists.length})`}
        <div className={cn.iconWrapper}>
          {collapsed ? <IconUp /> : <IconDown />}
        </div>
      </button>
      {collapsed && (
        <ul className={cn.checkedBlockLists}>
          {checkedBlockLists.map((item) => (
            <li key={item.id}>
              <IconSelected />
              {item.name}
            </li>
          ))}
        </ul>
      )}
      {monitoringWarningGroups && monitoringWarningGroups?.length > 0 && (
        <MonitoringWarningGroups
          groups={monitoringWarningGroups}
          currentEmployee={employee}
          applyWarning={applyWarning}
          transactionsTypes={transactionsTypes}
          applyWarningInProgress={applyWarningInProgress}
          activePermissions={activePermissions}
          onResetTransactionWarningAction={onResetTransactionWarningAction}
          resetPaymentOrderTransactionActionIsLoading={
            resetPaymentOrderTransactionActionIsLoading
          }
          resetPaymentOrderTransactionActionIsSuccess={
            resetPaymentOrderTransactionActionIsSuccess
          }
          selectedWarningId={selectedWarningId}
          getPaymentOrderWarningsAction={getPaymentOrderWarningsAction}
          selectedWarningGroup={selectedWarningGroup}
          paginationInProgress={paginationInProgress}
          hideAll={hideAll}
          showPassed={showPassed}
          setHiddenGroupsIds={setHiddenGroupsIds}
          hiddenGroupsIds={hiddenGroupsIds}
          getWarningGroupBeneficiaries={getWarningGroupBeneficiaries}
          beneficiariesIsLoading={beneficiariesIsLoading}
          warningsInfo={warningsInfo}
          getPaymentOrderWarningById={getPaymentOrderWarningById}
          isWarningByIdLoading={isWarningByIdLoading}
          isWarningByIdFetching={isWarningByIdFetching}
          selectedMonitoringRuleWarningId={selectedMonitoringRuleWarningId}
          hideApproveActions={hideApproveActions}
          hideRejectActions={hideRejectActions}
        />
      )}
      {applyError && <div className={cn.applyError}>{applyError}</div>}

      <Popup
        title="Select status and action"
        description="This will be applied to all matches in the group"
        onCancelClick={() => {
          setApplyAllPopupVisible(false);
          setComment('');
          setMatchStatus('');
          setAction('');
        }}
        onOkClick={onApplyActions}
        cancelButtonText="Cancel"
        okButtonText={
          batchApplyWarningInProgress ? 'Loading...' : 'Apply to all'
        }
        okButtonDisabled={
          !comment || !action || !matchStatus || batchApplyWarningInProgress
        }
        isVisible={applyAllPopupVisible}
        parent={document.getElementById('apply-all-popup') as HTMLElement}
      >
        <div className={cn.applyAllContent}>
          <Select
            name="matchStatus"
            value={matchStatus}
            onChange={(e) => setMatchStatus(e.target.value as string)}
            list={[
              {
                name: 'False',
                value: MatchStatusEnum.FALSE,
              },
              {
                name: 'True',
                value: MatchStatusEnum.TRUE,
              },
              {
                name: 'Potential',
                value: MatchStatusEnum.POTENTIAL,
              },
            ]}
            placeholder="Select Match status"
            disabled={
              !warningsWithoutActions.length || batchApplyWarningInProgress
            }
            className={cn.matchStatusSelect}
            fullWidth
          />
          <Select
            name="actions"
            value={action}
            onChange={(e) => setAction(e.target.value as string)}
            list={[
              {
                name: 'Pass all time for this client',
                value:
                  TransactionWarningActionEnum.PASS_ALL_TIME_FOR_THIS_CLIENT,
                hidden: !activePermissions?.includes(
                  PermissionEnum.TRANSACTIONS_INCOMING_AML_APPROVE_ACTION,
                ),
              },
              {
                name: 'Pass combination',
                value: TransactionWarningActionEnum.PASS_COMBINATION,
                hidden: !activePermissions?.includes(
                  PermissionEnum.TRANSACTIONS_INCOMING_AML_APPROVE_ACTION,
                ),
              },
              {
                name: 'Pass once',
                value: TransactionWarningActionEnum.PASS_ONCE,
                hidden: !activePermissions?.includes(
                  PermissionEnum.TRANSACTIONS_INCOMING_AML_APPROVE_ACTION,
                ),
              },
              {
                name: 'Stop',
                value: TransactionWarningActionEnum.STOP,
                hidden: !activePermissions?.includes(
                  PermissionEnum.TRANSACTIONS_INCOMING_AML_REJECT_ACTION,
                ),
              },
            ]}
            placeholder="Select actions"
            className={cn.matchStatusSelect}
            disabled={
              !warningsWithoutActions.length || batchApplyWarningInProgress
            }
            fullWidth
          />
          <Textarea
            name="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Enter a comment..."
            disabled={
              !warningsWithoutActions.length || batchApplyWarningInProgress
            }
          />
        </div>
      </Popup>
    </div>
  );
}
